import {PDFDocumentProxy} from 'pdfjs-dist';

/**
 * The PDF preview is fetched directly by pdf.js from the download URL, not
 * through the generated API client, so a DocumentNotFoundCondition response
 * never reaches the global condition handlers. pdf.js instead rejects the
 * document load with one of these error shapes, which we inspect to surface a
 * "document not found" message ourselves.
 */
type PdfLoadError = {name?: string; status?: number};

// The backend maps a missing or inaccessible document to HTTP 400
// (DocumentNotFoundCondition); 403/404 are covered defensively for other
// document sources. pdf.js reports a 404 as a named MissingPDFException.
const UNAVAILABLE_DOCUMENT_STATUSES = [400, 403, 404];

export const isDocumentUnavailableLoadError = (error: unknown): boolean => {
  const {name, status} = (error ?? {}) as PdfLoadError;
  return (
    name === 'MissingPDFException' ||
    (typeof status === 'number' &&
      UNAVAILABLE_DOCUMENT_STATUSES.includes(status))
  );
};

/**
 * react-pdf destroys a document as soon as the viewer switches to another file,
 * but it destroys it in a `finally` on the load promise — so a document that was
 * abandoned while still loading is reported as loaded first and destroyed right
 * afterwards. Anything working with such a document fails, because pdf.js drops
 * the worker message handler it talks through.
 *
 * pdf.js has no public flag for this; the transport does set `destroyed` before
 * it drops the message handler, which makes it a reliable substitute.
 */
export function isDestroyedPdfDocument(document: PDFDocumentProxy): boolean {
  return Boolean(
    (document as unknown as {_transport?: {destroyed?: boolean}})._transport
      ?.destroyed
  );
}
